import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL = 5 * 60 * 1000

export const CATEGORY_OPTIONS = [
  { value: 'on_site', label: 'On site' },
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
]

export const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'published', label: 'Live' },
  { value: 'draft', label: 'Drafts' },
  { value: 'expired', label: 'Past deadline' },
]

// GET requests go through axios.get(url, { headers }) — query params must be
// embedded in the URL itself, they aren't sent from a request body.
function buildQuery(state) {
  const parts = [`page[number]=${state.page.number}`, `page[limit]=${state.page.limit}`]
  if (state.search) parts.push(`search=${encodeURIComponent(state.search)}`)
  if (state.status) parts.push(`status=${encodeURIComponent(state.status)}`)
  return parts.join('&')
}

// Roles listed at /careers. See
// app/controllers/api/v1/admin/career_jobs_controller.rb.
export const useAdminCareerJobsStore = defineStore('adminCareerJobs', {
  state: () => ({
    records: [],
    counts: { total: 0, published: 0, draft: 0, expired: 0 },
    page: { current_page: 1, total_pages: 1, total_records: 0, number: 1, limit: 20 },
    search: '',
    status: '',
    fetchedAt: null,
    loading: false,
    error: null,
  }),

  getters: {
    isCacheValid: (state) =>
      state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,
  },

  actions: {
    async fetchIfStale() {
      if (this.isCacheValid && this.records.length > 0) return
      await this.fetchList()
    },

    async fetchList() {
      this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/admin/career_jobs?${buildQuery(this)}`, {}, false)
        if (!res.js_error && res.response.success) {
          this.records = res.response.data.records
          this.page = { ...this.page, ...res.response.data.page }
          this.counts = res.response.data.counts
          this.fetchedAt = Date.now()
        } else {
          this.error = 'Failed to load roles.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    async create(payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', '/api/v1/admin/career_jobs', { career_job: payload }, false)
      if (!res.js_error && res.response.success) this.invalidate()
      return res
    },

    async update(id, payload) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/admin/career_jobs/${id}`, { career_job: payload }, false)
      if (!res.js_error && res.response.success) this.applyRecord(res.response.data)
      return res
    },

    async publish(id) {
      return this.togglePublish(id, 'publish')
    },

    async unpublish(id) {
      return this.togglePublish(id, 'un_publish')
    },

    async togglePublish(id, action) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/admin/career_jobs/${id}/${action}`, {}, false)
      if (!res.js_error && res.response.success) {
        this.applyRecord(res.response.data)
        this.invalidate()
      }
      return res
    },

    // Refused by the server when the role has applicants, since deleting it
    // would take their CVs and contact details with it.
    async destroy(id) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `/api/v1/admin/career_jobs/${id}`, {}, false)
      if (!res.js_error && res.response.success) {
        this.records = this.records.filter((r) => r.id !== id)
        this.invalidate()
      }
      return res
    },

    async fetchApplicants(id) {
      const { axiosRequest } = useRequest()
      return axiosRequest('GET', `/api/v1/admin/career_jobs/${id}/applicants`, {}, false)
    },

    applyRecord(updated) {
      const index = this.records.findIndex((r) => r.id === updated.id)
      if (index !== -1) this.records[index] = { ...this.records[index], ...updated }
    },

    invalidate() {
      this.fetchedAt = null
    },

    setSearch(val) {
      this.search = val
      this.page.number = 1
      this.invalidate()
    },

    setStatus(val) {
      this.status = val
      this.page.number = 1
      this.invalidate()
    },

    goToPage(number) {
      this.page.number = number
      this.invalidate()
    },
  },
})
